import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { UserPlus, Users } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

interface EnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  courseId: string;
  courseName: string;
}

interface User {
  id: string;
  full_name: string;
  email: string;
  role: string;
}

export function EnrollmentModal({ isOpen, onClose, onSuccess, courseId, courseName }: EnrollmentModalProps) {
  const [alumnos, setAlumnos] = useState<User[]>([]);
  const [docentes, setDocentes] = useState<User[]>([]);
  const [enrolledAlumnos, setEnrolledAlumnos] = useState<string[]>([]);
  const [selectedDocente, setSelectedDocente] = useState<string>('');
  const [currentDocente, setCurrentDocente] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadUsers();
      loadEnrollments();
      loadCourseDocente();
    }
  }, [isOpen, courseId]);

  const loadUsers = async () => {
    try {
      const [alumnosRes, docentesRes] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, full_name, email, role')
          .eq('role', 'alumno')
          .eq('active', true)
          .order('full_name'),
        supabase
          .from('profiles')
          .select('id, full_name, email, role')
          .eq('role', 'docente')
          .eq('active', true)
          .order('full_name'),
      ]);

      if (alumnosRes.error) throw alumnosRes.error;
      if (docentesRes.error) throw docentesRes.error;

      setAlumnos(alumnosRes.data || []);
      setDocentes(docentesRes.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadEnrollments = async () => {
    try {
      const { data, error } = await supabase
        .from('course_enrollments')
        .select('alumno_id')
        .eq('course_id', courseId)
        .eq('status', 'active');

      if (error) throw error;
      setEnrolledAlumnos(data?.map((e) => e.alumno_id) || []);
    } catch (error) {
      console.error('Error loading enrollments:', error);
    }
  };

  const loadCourseDocente = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('docente_id')
        .eq('id', courseId)
        .single();

      if (error) throw error;
      if (data?.docente_id) {
        setCurrentDocente(data.docente_id);
        setSelectedDocente(data.docente_id);
      }
    } catch (error) {
      console.error('Error loading course docente:', error);
    }
  };

  const toggleEnrollment = async (alumnoId: string) => {
    try {
      const isEnrolled = enrolledAlumnos.includes(alumnoId);

      if (isEnrolled) {
        const { error } = await supabase
          .from('course_enrollments')
          .delete()
          .eq('course_id', courseId)
          .eq('alumno_id', alumnoId);

        if (error) throw error;
        setEnrolledAlumnos(enrolledAlumnos.filter((id) => id !== alumnoId));
      } else {
        const { error } = await supabase
          .from('course_enrollments')
          .insert({
            course_id: courseId,
            alumno_id: alumnoId,
            status: 'active',
          });

        if (error) throw error;
        setEnrolledAlumnos([...enrolledAlumnos, alumnoId]);
      }
    } catch (error: any) {
      console.error('Error toggling enrollment:', error);
      setError(error.message || 'Error al actualizar inscripción');
    }
  };

  const handleChangeDocente = async () => {
    if (!selectedDocente || selectedDocente === currentDocente) return;

    setLoading(true);
    setError('');

    try {
      const { error } = await supabase
        .from('courses')
        .update({ docente_id: selectedDocente })
        .eq('id', courseId);

      if (error) throw error;

      const { error: modulesError } = await supabase
        .from('modules')
        .update({ docente_id: selectedDocente })
        .eq('course_id', courseId);

      if (modulesError) throw modulesError;

      setCurrentDocente(selectedDocente);
      onSuccess();
    } catch (error: any) {
      console.error('Error changing docente:', error);
      setError(error.message || 'Error al cambiar docente');
    } finally {
      setLoading(false);
    }
  };

  const filteredAlumnos = alumnos.filter((alumno) =>
    alumno.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    alumno.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Inscripciones del curso"
      description={courseName}
      size="xl"
      footer={<Button variant="glass" fullWidth onClick={onClose}>Cerrar</Button>}
    >
      <div className="space-y-6">
        {error && (
          <div role="alert" className="p-3 bg-red-500/10 border border-red-500/40 rounded-lg text-red-300 text-sm">
            {error}
          </div>
        )}

        <section className="bg-black/30 rounded-lg p-4 border border-white/[0.08]">
          <div className="flex items-center gap-3 mb-3">
            <Users className="w-5 h-5 text-cubus-lime" aria-hidden="true" />
            <h3 className="font-semibold text-white">Docente asignado</h3>
          </div>
          <div className="flex gap-3">
            <label htmlFor="enroll-docente" className="sr-only">Docente</label>
            <select
              id="enroll-docente"
              value={selectedDocente}
              onChange={(e) => setSelectedDocente(e.target.value)}
              className="flex-1 px-4 py-2 bg-white/[0.05] border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime/60"
            >
              <option value="">Seleccionar docente</option>
              {docentes.map((docente) => (
                <option key={docente.id} value={docente.id}>
                  {docente.full_name}
                </option>
              ))}
            </select>
            {selectedDocente !== currentDocente && (
              <Button variant="lime" loading={loading} onClick={handleChangeDocente}>
                Cambiar
              </Button>
            )}
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <UserPlus className="w-5 h-5 text-cubus-lime" aria-hidden="true" />
              <h3 className="font-semibold text-white">
                Alumnos inscritos ({enrolledAlumnos.length})
              </h3>
            </div>
          </div>

          <label htmlFor="enroll-search" className="sr-only">Buscar alumnos</label>
          <input
            id="enroll-search"
            type="text"
            placeholder="Buscar alumnos…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 mb-4 bg-black/30 border border-white/[0.08] rounded-lg text-white placeholder:text-subtle focus:outline-none focus:ring-2 focus:ring-cubus-lime/60"
          />

          <ul className="space-y-2 max-h-96 overflow-y-auto" aria-label="Lista de alumnos">
            {filteredAlumnos.map((alumno) => {
              const isEnrolled = enrolledAlumnos.includes(alumno.id);
              return (
                <li
                  key={alumno.id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                    isEnrolled
                      ? 'bg-cubus-lime/10 border-cubus-lime/40'
                      : 'bg-black/30 border-white/[0.08] hover:border-white/[0.16]'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E8FF30] to-[#C4E600] flex items-center justify-center text-cubus-bg font-bold shrink-0">
                      {alumno.full_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">{alumno.full_name}</p>
                      <p className="text-sm text-subtle truncate">{alumno.email}</p>
                    </div>
                  </div>
                  <Button
                    variant={isEnrolled ? 'danger' : 'lime'}
                    size="sm"
                    onClick={() => toggleEnrollment(alumno.id)}
                    aria-pressed={isEnrolled}
                  >
                    {isEnrolled ? 'Desinscribir' : 'Inscribir'}
                  </Button>
                </li>
              );
            })}
          </ul>

          {filteredAlumnos.length === 0 && (
            <p className="text-center text-muted py-8">No se encontraron alumnos.</p>
          )}
        </section>
      </div>
    </Modal>
  );
}
